/**
 * findExistingQueryTab — host-tab lookup for the AI task launchers (ruling F0.4).
 *
 * AI chat is tab-scoped, so a launcher fired from a surface that is not a query
 * tab (Object Explorer, Schema Overview, a compare result) has to land on one.
 * Reusing a tab the user already has open on that connection beats opening a
 * new one every time.
 *
 * When the caller names a database the match must be exact: reusing a tab bound
 * to another database would send the agent the schema of the wrong database,
 * which is worse than one extra tab.
 *
 * `preferTabIds` is the amended half of F0.4. First-match-wins is wrong as soon
 * as two query tabs share a connection: a user in Query-2 firing "Optimize this
 * query" would be yanked into Query-1, whose buffer is what reaches the system
 * prompt and whose `tabId` is what the agent gets for `ui_set_editor_text` — so
 * it optimises the wrong SQL and can overwrite an unsaved buffer nobody
 * targeted. Callers pass, in order: the tab the launcher fired from (when the
 * surface knows it) and the active tab.
 *
 * Shared with T12/T13 so every launcher call site resolves its host tab the
 * same way.
 */

import { isQueryTab } from '../../workspace/core/types/workspaceTypes';
import type { QueryTab, Tab } from '../../workspace/core/types/workspaceTypes';

export function findExistingQueryTab(
  tabs: Tab[],
  connectionId: string,
  database?: string,
  preferTabIds: readonly (string | null | undefined)[] = [],
): QueryTab | undefined {
  const candidates = tabs.filter(
    (tab): tab is QueryTab =>
      isQueryTab(tab) &&
      tab.connectionInfo?.connectionId === connectionId &&
      (!database || tab.connectionInfo?.database === database),
  );

  for (const preferred of preferTabIds) {
    if (!preferred) continue;
    const match = candidates.find((tab) => tab.id === preferred);
    if (match) return match;
  }

  return candidates[0];
}
